import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  ArrowLeft, Clock, CheckCircle, XCircle, AlertTriangle, FileText,
  User, Calendar, MessageSquare, RotateCcw, Eye
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate } from '../../utils/helpers';
import OTPVerificationCompact from '../../components/OTPVerificationCompact';

const CancellationRequestView = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // UI state
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewAction, setReviewAction] = useState('');
  const [reviewComments, setReviewComments] = useState('');
  const [forwardToUser, setForwardToUser] = useState('');
  const [forwardToLevel, setForwardToLevel] = useState('');
  const [showOTP, setShowOTP] = useState(false);

  // Fetch cancellation request details
  const { data: cancellationRequestData, isLoading } = useQuery(
    ['cancellationRequest', requestId],
    async () => {
      const response = await api.get(`/cancellation-requests/${requestId}`);
      return response.data;
    }
  );

  // Fetch available reviewers
  const { data: reviewersData } = useQuery(
    'reviewers',
    async () => {
      const response = await api.get('/users/reviewers');
      return response.data.users;
    }
  );

  const cancellationRequest = cancellationRequestData?.cancellationRequest;
  const history = cancellationRequestData?.history || [];

  // Check if user can review
  const canReview = useMemo(() => {
    if (!cancellationRequest || !user) return false;

    switch (cancellationRequest.status) {
      case 'Pending_EAC_Review':
        return user.role === 'eac' && cancellationRequest.assignedEAC === user.id;
      case 'Pending_SDO_Review':
        return user.role === 'sdo' && cancellationRequest.assignedSDO === user.id;
      case 'Pending_ADC_Review':
        return user.role === 'adc' && cancellationRequest.assignedADC === user.id;
      case 'Pending_DC_Approval':
        return user.role === 'dc';
      default:
        return false;
    }
  }, [cancellationRequest, user]);

  // Check if ADC can give final approval (only if they are the subdivision head)
  const canAdcFinalApprove = useMemo(() => {
    if (!cancellationRequest || !user) return false;

    return user.role === 'adc' &&
           cancellationRequest.status === 'Pending_ADC_Review' &&
           cancellationRequest.assignedADC === user.id &&
           user.isSubdivisionHead;
  }, [cancellationRequest, user]);

  // Check if user is the initiator
  const isInitiator = useMemo(() => {
    return cancellationRequest && user && cancellationRequest.initiatedBy === user.id;
  }, [cancellationRequest, user]);

  // Role hierarchy for filtering reviewers
  const ROLE_HIERARCHY = ['bo', 'eac', 'sdo', 'adc', 'dc'];

  // Get available reviewers with higher roles in same subdivision + all DC users
  const availableReviewers = useMemo(() => {
    if (!reviewersData || !user || !cancellationRequest?.permit) return [];

    const currentRoleIndex = ROLE_HIERARCHY.indexOf(user.role);
    const subdivisionId = cancellationRequest.permit.subdivisionId;

    return reviewersData.filter(u => {
      const reviewerRoleIndex = ROLE_HIERARCHY.indexOf(u.role);

      // Must be a higher role
      if (reviewerRoleIndex <= currentRoleIndex) return false;

      // DC users always included
      if (u.role === 'dc') return true;

      // Other higher roles must be in same subdivision
      return u.subdivisionId === subdivisionId;
    });
  }, [reviewersData, user, cancellationRequest?.permit?.subdivisionId]);

  // Group reviewers by role
  const reviewersByRole = useMemo(() => {
    const grouped = {};
    const roleOrder = ['eac', 'sdo', 'adc', 'dc'];

    availableReviewers.forEach(reviewer => {
      const role = reviewer.role;
      if (!grouped[role]) grouped[role] = [];
      grouped[role].push(reviewer);
    });

    return roleOrder
      .filter(role => grouped[role] && grouped[role].length > 0)
      .map(role => ({
        level: role,
        label: role.toUpperCase(),
        users: grouped[role]
      }));
  }, [availableReviewers]);

  // Review mutation
  const reviewMutation = useMutation(
    async ({ action, comments, forwardToUser, otpCode }) => {
      const response = await api.post(`/cancellation-requests/${requestId}/review`, {
        action,
        comments,
        forwardToUser,
        otpCode
      });
      return response.data;
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['cancellationRequest', requestId]);
        queryClient.invalidateQueries(['pendingCancellations']);
        setShowReviewDialog(false);
        setShowOTP(false);
        setReviewComments('');
        setForwardToUser('');
        setForwardToLevel('');

        if (data.message) {
          toast.success(data.message);
        }

        // Redirect to cancellation requests list if approved/rejected
        if (variables.action === 'approve' || variables.action === 'final_approve' || variables.action === 'reject') {
          navigate('/cancellation-requests');
        }
      },
      onError: (error) => {
        if (error.response?.data?.requiresOTP) {
          setShowOTP(true);
        } else {
          toast.error(error.response?.data?.message || error.message);
        }
      }
    }
  );

  // Cancel mutation (initiator cancels their own request)
  const cancelMutation = useMutation(
    async () => {
      const response = await api.post(`/cancellation-requests/${requestId}/cancel`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['cancellationRequest', requestId]);
        toast.success('Cancellation request has been cancelled');
        navigate('/cancellation-requests');
      }
    }
  );

  const handleReviewAction = (action) => {
    setReviewAction(action);
    setShowReviewDialog(true);
    setReviewComments('');
    setForwardToUser('');
    setForwardToLevel('');
    // For DC approve or ADC final_approve, show OTP component immediately
    // (it has its own comments field, so no need for the separate comments step)
    const needsOTPImmediately = (action === 'approve' && user.role === 'dc') || action === 'final_approve';
    setShowOTP(needsOTPImmediately);
  };

  const handleSubmitReview = () => {
    if (!reviewComments.trim()) {
      toast.error('Please provide comments');
      return;
    }

    // For approve actions (non-DC), require reviewer selection
    if (reviewAction === 'approve' && user.role !== 'dc' && !forwardToUser) {
      toast.error('Please select a reviewer to forward this request');
      return;
    }

    reviewMutation.mutate({
      action: reviewAction,
      comments: reviewComments.trim(),
      forwardToUser: forwardToUser || undefined
    });
  };

  const handleOTPVerified = (otpComments, otpCode) => {
    // Submit review with OTP code for inline verification (single API call)
    reviewMutation.mutate({
      action: reviewAction,
      comments: (otpComments || reviewComments).trim(),
      otpCode,
      forwardToUser: forwardToUser || undefined
    });
  };

  const handleCancelRequest = () => {
    if (window.confirm('Are you sure you want to cancel this cancellation request? This action cannot be undone.')) {
      cancelMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!cancellationRequest) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Cancellation request not found</p>
        </div>
      </div>
    );
  }

  const permit = cancellationRequest.permit;
  const isPending = ['Pending_EAC_Review', 'Pending_SDO_Review', 'Pending_ADC_Review', 'Pending_DC_Approval'].includes(cancellationRequest.status);

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/cancellation-requests"
          className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Cancellation Requests
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Cancellation Request
            </h1>
            <p className="text-gray-600 mt-1">
              For Permit: <Link to={`/permits/${permit.id}`} className="text-primary-600 hover:underline">{permit.permit_no}</Link>
            </p>
          </div>
          <div>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              cancellationRequest.status === 'Approved' ? 'bg-green-100 text-green-800' :
              cancellationRequest.status === 'Rejected' ? 'bg-red-100 text-red-800' :
              cancellationRequest.status === 'Cancelled' ? 'bg-gray-100 text-gray-800' :
              'bg-yellow-100 text-yellow-800'
            }`}>
              {cancellationRequest.status.replace(/_/g, ' ')}
            </span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {isPending && canReview && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-sm font-medium text-blue-900 mb-1">
                Action Required
              </h3>
              <p className="text-sm text-blue-700">
                This cancellation request is assigned to you for review.
              </p>
            </div>
            <div className="flex gap-2 ml-4">
              {user.role !== 'dc' && (
                <button
                  onClick={() => handleReviewAction('approve')}
                  className="btn btn-sm btn-primary"
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Approve & Forward
                </button>
              )}
              {canAdcFinalApprove && (
                <button
                  onClick={() => handleReviewAction('final_approve')}
                  className="btn btn-sm bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Final Approve (ADC)
                </button>
              )}
              {user.role === 'dc' && (
                <button
                  onClick={() => handleReviewAction('approve')}
                  className="btn btn-sm bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Approve & Cancel Permit
                </button>
              )}
              <button
                onClick={() => handleReviewAction('reject')}
                className="btn btn-sm btn-danger"
              >
                <XCircle className="h-4 w-4 mr-1" />
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Initiator Cancel Button */}
      {isPending && isInitiator && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-1">
                Cancel This Request
              </h3>
              <p className="text-sm text-gray-600">
                You can cancel this cancellation request if it's no longer needed.
              </p>
            </div>
            <button
              onClick={handleCancelRequest}
              disabled={cancelMutation.isLoading}
              className="btn btn-sm btn-outline"
            >
              Cancel Request
            </button>
          </div>
        </div>
      )}

      {/* Permit Details */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Permit Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <label className="text-gray-500 text-xs font-medium">Permit Number</label>
            <p className="text-gray-900 font-medium">{permit.permit_no}</p>
          </div>
          <div>
            <label className="text-gray-500 text-xs font-medium">Current Status</label>
            <p>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                permit.status === 'Active' ? 'bg-green-100 text-green-800' :
                permit.status === 'Renewal_Needed' ? 'bg-yellow-100 text-yellow-800' :
                permit.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {permit.status === 'Renewal_Needed' ? 'Renewal Needed' : permit.status}
              </span>
            </p>
          </div>
          <div>
            <label className="text-gray-500 text-xs font-medium">Permit Holder</label>
            <p className="text-gray-900">{permit.name}</p>
          </div>
          <div>
            <label className="text-gray-500 text-xs font-medium">Village</label>
            <p className="text-gray-900">{permit.village}</p>
          </div>
          <div>
            <label className="text-gray-500 text-xs font-medium">Issue Date</label>
            <p className="text-gray-900">{formatDate(permit.issue_date)}</p>
          </div>
          {permit.subdivision && (
            <div>
              <label className="text-gray-500 text-xs font-medium">Subdivision</label>
              <p className="text-gray-900">{permit.subdivision.name}</p>
            </div>
          )}
          <div>
            <label className="text-gray-500 text-xs font-medium">Original Status (at request time)</label>
            <p className="text-gray-900">{cancellationRequest.originalStatus}</p>
          </div>
        </div>
      </div>

      {/* Cancellation Details */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Cancellation Details</h2>

        <div className="space-y-4">
          {/* Initiator */}
          <div>
            <label className="text-gray-500 text-xs font-medium">Initiated By</label>
            <p className="text-gray-900">
              {cancellationRequest.initiator?.fullName} ({cancellationRequest.initiatorRole.toUpperCase()})
            </p>
            <p className="text-xs text-gray-500">{formatDate(cancellationRequest.createdAt)}</p>
          </div>

          {/* Reason */}
          <div>
            <label className="text-gray-500 text-xs font-medium">Reason for Cancellation</label>
            <p className="text-gray-900 mt-1 whitespace-pre-wrap">{cancellationRequest.reason}</p>
          </div>

          {/* Supporting Documents */}
          {cancellationRequest.supportingDocuments && cancellationRequest.supportingDocuments.length > 0 && (
            <div>
              <label className="text-gray-500 text-xs font-medium mb-2 block">Supporting Documents</label>
              <div className="space-y-2">
                {cancellationRequest.supportingDocuments.map((doc, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center flex-1 min-w-0">
                      <FileText className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-900 truncate">{doc.fileName}</span>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const response = await api.get(`/cancellation-requests/${requestId}/documents/download?index=${index}`);
                          window.open(response.data.downloadUrl, '_blank');
                        } catch (error) {
                          toast.error('Failed to open document');
                        }
                      }}
                      className="ml-3 inline-flex items-center px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors flex-shrink-0"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Return Reason Banner */}
      {(() => {
        const returnEntry = history.find(e => e.action === 'returned');
        if (!returnEntry) return null;
        return (
          <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
            <div className="flex items-start">
              <RotateCcw className="h-5 w-5 text-orange-500 mr-3 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-orange-800">
                  Request Returned by {returnEntry.performer?.role?.toUpperCase()} - {returnEntry.performer?.fullName}
                </h3>
                <div className="mt-2 p-3 bg-white rounded border border-orange-200">
                  <p className="text-sm text-gray-800">{returnEntry.comments}</p>
                </div>
                <p className="mt-2 text-xs text-orange-600">
                  Returned on {formatDate(returnEntry.created_at)}
                </p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Review Comments */}
      {(cancellationRequest.eacComments || cancellationRequest.sdoComments ||
        cancellationRequest.adcComments || cancellationRequest.rejectionReason) && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Review Comments</h2>
          <div className="space-y-4">
            {cancellationRequest.eacComments && (
              <div className="border-l-4 border-blue-400 pl-4">
                <p className="text-xs font-medium text-gray-500">EAC Review</p>
                <p className="text-sm text-gray-900 mt-1">{cancellationRequest.eacComments}</p>
                {cancellationRequest.reviewedByEAC && (
                  <p className="text-xs text-gray-500 mt-1">
                    Reviewed on {formatDate(cancellationRequest.reviewedAtEAC)}
                  </p>
                )}
              </div>
            )}
            {cancellationRequest.sdoComments && (
              <div className="border-l-4 border-purple-400 pl-4">
                <p className="text-xs font-medium text-gray-500">SDO Review</p>
                <p className="text-sm text-gray-900 mt-1">{cancellationRequest.sdoComments}</p>
                {cancellationRequest.reviewedBySDO && (
                  <p className="text-xs text-gray-500 mt-1">
                    Reviewed on {formatDate(cancellationRequest.reviewedAtSDO)}
                  </p>
                )}
              </div>
            )}
            {cancellationRequest.adcComments && (
              <div className="border-l-4 border-indigo-400 pl-4">
                <p className="text-xs font-medium text-gray-500">ADC Review</p>
                <p className="text-sm text-gray-900 mt-1">{cancellationRequest.adcComments}</p>
                {cancellationRequest.reviewedByADC && (
                  <p className="text-xs text-gray-500 mt-1">
                    Reviewed on {formatDate(cancellationRequest.reviewedAtADC)}
                  </p>
                )}
              </div>
            )}
            {cancellationRequest.rejectionReason && (
              <div className="border-l-4 border-red-400 pl-4">
                <p className="text-xs font-medium text-gray-500">Rejection Reason</p>
                <p className="text-sm text-gray-900 mt-1">{cancellationRequest.rejectionReason}</p>
                {cancellationRequest.rejectedAt && (
                  <p className="text-xs text-gray-500 mt-1">
                    Rejected on {formatDate(cancellationRequest.rejectedAt)}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* History Timeline */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">History</h2>
        <div className="space-y-4">
          {history.map((entry, index) => (
            <div key={entry.id} className="flex items-start">
              <div className="flex-shrink-0">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  entry.action === 'created' ? 'bg-blue-100' :
                  entry.action === 'reviewed' ? 'bg-purple-100' :
                  entry.action === 'approved' ? 'bg-green-100' :
                  entry.action === 'rejected' ? 'bg-red-100' :
                  'bg-gray-100'
                }`}>
                  {entry.action === 'created' && <FileText className="h-5 w-5 text-blue-600" />}
                  {entry.action === 'reviewed' && <MessageSquare className="h-5 w-5 text-purple-600" />}
                  {entry.action === 'approved' && <CheckCircle className="h-5 w-5 text-green-600" />}
                  {entry.action === 'rejected' && <XCircle className="h-5 w-5 text-red-600" />}
                  {(entry.action === 'returned' || entry.action === 'cancelled') && <RotateCcw className="h-5 w-5 text-gray-600" />}
                </div>
              </div>
              <div className="ml-4 flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {entry.action === 'created' && 'Cancellation Request Created'}
                      {entry.action === 'reviewed' && 'Reviewed and Forwarded'}
                      {entry.action === 'approved' && 'Approved - Permit Cancelled'}
                      {entry.action === 'rejected' && 'Rejected'}
                      {entry.action === 'returned' && 'Returned to Initiator'}
                      {entry.action === 'cancelled' && 'Request Cancelled'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      by {entry.performer?.fullName} ({entry.performer?.role?.toUpperCase()})
                    </p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-xs text-gray-500">{formatDate(entry.created_at)}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(entry.created_at).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                {entry.previousStatus && (
                  <p className="text-xs text-gray-600 mt-1">
                    Status: {entry.previousStatus} → {entry.newStatus}
                  </p>
                )}
                {entry.comments && (
                  <p className="text-sm text-gray-700 mt-2 p-3 bg-gray-50 rounded">
                    {entry.comments}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Review Dialog */}
      {showReviewDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {reviewAction === 'approve' && user?.role !== 'dc' && 'Approve & Forward'}
                {reviewAction === 'final_approve' && 'Final Approve (ADC)'}
                {reviewAction === 'approve' && user?.role === 'dc' && 'Approve & Cancel Permit'}
                {reviewAction === 'reject' && 'Reject Cancellation Request'}
              </h2>

              {reviewAction === 'approve' && user?.role === 'dc' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-red-400 mr-3 mt-0.5" />
                    <div>
                      <p className="text-sm text-red-800">
                        <strong>Warning:</strong> Approving this cancellation request will permanently cancel the permit.
                        This action cannot be undone. OTP verification is required.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {reviewAction === 'final_approve' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-red-400 mr-3 mt-0.5" />
                    <div>
                      <p className="text-sm text-red-800">
                        <strong>Warning:</strong> As ADC heading this subdivision, you can give final approval to cancel this permit.
                        This action is permanent and requires OTP verification.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* OTP Component */}
              {showOTP && (reviewAction === 'approve' && user?.role === 'dc' || reviewAction === 'final_approve') ? (
                <div className="mb-4">
                  <OTPVerificationCompact
                    permitId={cancellationRequest.permitId}
                    purpose="permit_cancellation"
                    skipVerification={true}
                    onSuccess={handleOTPVerified}
                    onCancel={() => { setShowOTP(false); setShowReviewDialog(false); }}
                    isLoading={reviewMutation.isLoading}
                  />
                </div>
              ) : (
                <>
                  {/* Forward Selection (for non-DC approve) */}
                  {reviewAction === 'approve' && user?.role !== 'dc' && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Forward To <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={forwardToUser}
                        onChange={(e) => {
                          const selectedUser = availableReviewers.find(u => u.id === parseInt(e.target.value));
                          setForwardToUser(e.target.value);
                          setForwardToLevel(selectedUser?.role || '');
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Select Reviewer</option>
                        {reviewersByRole.map(group => (
                          <optgroup key={group.level} label={group.label}>
                            {group.users.map(u => (
                              <option key={u.id} value={u.id}>
                                {u.fullName} ({u.designation || u.role.toUpperCase()})
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Select a reviewer with a higher role to forward this request.
                      </p>
                    </div>
                  )}

                  {/* Comments */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Comments <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={reviewComments}
                      onChange={(e) => setReviewComments(e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder={
                        reviewAction === 'reject' ? 'Explain why this cancellation request is being rejected...' :
                        reviewAction === 'return' ? 'Explain why this request is being returned...' :
                        'Add your review comments...'
                      }
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowReviewDialog(false)}
                      className="btn btn-outline"
                      disabled={reviewMutation.isLoading}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmitReview}
                      disabled={reviewMutation.isLoading || !reviewComments.trim()}
                      className={`btn ${
                        reviewAction === 'reject' ? 'btn-danger' :
                        reviewAction === 'approve' || reviewAction === 'final_approve' ? 'bg-green-600 hover:bg-green-700 text-white' :
                        'btn-primary'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {reviewMutation.isLoading ? 'Processing...' :
                       reviewAction === 'approve' && user?.role !== 'dc' ? 'Approve & Forward' :
                       reviewAction === 'final_approve' ? 'Final Approve (ADC)' :
                       reviewAction === 'approve' && user?.role === 'dc' ? 'Approve & Cancel Permit' :
                       'Reject'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CancellationRequestView;
